import { Icon, IconSVG, SVGColorKey } from "@studio3t/generic-components";
import { deriveJobControlState } from "@/pages/pipelineControls";
import type { NormalizedJobState } from "@/pages/pipelineHelpers";
import {
  FlowHeader,
  FlowHeaderActions,
  FlowHeaderLeft,
  FlowHeaderTitle,
  SegmentedButton,
  SegmentedGroup,
  StudioButton,
} from "./styles";

interface PipelineFlowHeaderProps {
  name: string;
  state: NormalizedJobState;
  desiredState: NormalizedJobState;
  onStart: () => void;
  onStop: () => void;
  onOpenInStudio: () => void;
  starting?: boolean;
  stopping?: boolean;
  importing?: boolean;
  /** Offered only when the source keeps a position and nothing reads it. */
  reset?: { allowed: boolean; reason?: string; onReset: () => void };
}

export function PipelineFlowHeader({
  name,
  state,
  desiredState,
  onStart,
  onStop,
  onOpenInStudio,
  starting,
  stopping,
  importing,
  reset,
}: PipelineFlowHeaderProps) {
  const { startDisabled, stopDisabled } = deriveJobControlState(
    state,
    desiredState,
    { starting, stopping },
  );

  return (
    <FlowHeader>
      <FlowHeaderLeft>
        <IconSVG
          icon={Icon.Trace}
          size={16}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
        <FlowHeaderTitle title={name}>{name}</FlowHeaderTitle>
      </FlowHeaderLeft>

      <FlowHeaderActions>
        <SegmentedGroup>
          <SegmentedButton
            type="button"
            $side="left"
            $active
            aria-label="Start"
            onClick={onStart}
            disabled={startDisabled}
          >
            <IconSVG
              icon={Icon.Run}
              size={16}
              svgColorKey={SVGColorKey.buttonPrimaryIcon}
            />
          </SegmentedButton>
          <SegmentedButton
            type="button"
            $side="right"
            aria-label="Stop"
            onClick={onStop}
            disabled={stopDisabled}
          >
            <IconSVG
              icon={Icon.Stop}
              size={16}
              svgColorKey={SVGColorKey.buttonPrimaryIcon}
            />
          </SegmentedButton>
        </SegmentedGroup>

        {reset && (
          <StudioButton
            type="button"
            onClick={reset.onReset}
            disabled={!reset.allowed}
            title={reset.reason}
          >
            <IconSVG
              icon={Icon.Refresh}
              size={16}
              svgColorKey={SVGColorKey.buttonSecondaryIcon}
            />
            Reset position
          </StudioButton>
        )}

        <StudioButton
          type="button"
          onClick={onOpenInStudio}
          disabled={importing}
        >
          <IconSVG
            icon={Icon.Link}
            size={16}
            svgColorKey={SVGColorKey.buttonSecondaryIcon}
          />
          Open in Studio
        </StudioButton>
      </FlowHeaderActions>
    </FlowHeader>
  );
}
